import { useState } from "react";
import TargetingReportIcon from "../../assets/icons/targeting-report.svg";
import { FiDownload } from "react-icons/fi";

interface ScopeDisplayProps {
    scope?: string;
    assetName?: string;
    targetValues?: string[];
    endpointValues?: string[];
    targetEndpointMap?: Record<string, string[]>; // target_value → endpoint_values
    targetMetadataMap?: Record<string, any>; // target_value → target metadata
    programAssetName?: string;
    programTitle?: string;
    theme?: "light" | "dark";
    showLabel?: boolean;
    isV1?: boolean;
}

const ScopeDisplay = ({
    scope,
    assetName,
    targetValues,
    endpointValues,
    targetEndpointMap,
    targetMetadataMap,
    programAssetName,
    programTitle,
    theme = "dark",
    showLabel = true,
    isV1 = false
}: ScopeDisplayProps) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // Theme-based styles
    const isDark = theme === "dark";
    const labelColor = isDark ? "text-white" : "text-gray-900";
    const separatorColor = isDark ? "text-gray-500" : "text-gray-400";
    const readMoreColor = isDark ? "text-blue-400 hover:text-white" : "text-blue-600 hover:text-blue-800";

    const assetClass = isDark
        ? "bg-blue-900/50 text-blue-200 border-blue-700/30"
        : "bg-blue-50 text-blue-700 border-blue-100";

    const targetClass = isDark
        ? "bg-indigo-900/40 text-indigo-300 border-indigo-700/30"
        : "bg-indigo-50 text-indigo-700 border-indigo-100";

    const endpointClass = isDark
        ? "bg-emerald-900/40 text-[#58CC02] border-[#58CC02]/40"
        : "bg-emerald-50 text-emerald-700 border-emerald-100";

    const targetLabelClass = isDark
        ? "text-indigo-400 text-[9px] font-bold uppercase tracking-wider"
        : "text-indigo-500 text-[9px] font-bold uppercase tracking-wider";

    // Helper to render asset pill
    const renderAsset = (name: string) => (
        <span
            title="Asset Name"
            className={`text-xs font-semibold px-2 py-0.5 rounded h-fit border shadow-sm ${assetClass}`}
        >
            {name}
        </span>
    );

    // Helper to render target pill
    const renderTarget = (value: string) => {
        const metadata = targetMetadataMap?.[value];
        const binaryUrl = metadata?.binary_url || metadata?.binaryUrl;

        const binaryName = metadata?.binary_name || metadata?.binaryName;
        const isFallbackValue = value === "Uploaded Binary" || value === binaryName;

        return (
            <div className="flex items-center gap-1.5 flex-wrap">
                {(!isFallbackValue || !binaryUrl) && (
                    <span
                        title="Target Value"
                        className={`text-[11px] font-medium px-2 py-0.5 rounded border shadow-sm ${targetClass}`}
                    >
                        {value}
                    </span>
                )}
                {binaryUrl && (
                    <a
                        href={binaryUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-[9px] font-bold text-blue-400 hover:text-white bg-blue-900/40 px-1.5 py-0.5 rounded border border-blue-700/30 transition-colors h-fit"
                        onClick={(e) => e.stopPropagation()}
                        title={binaryName || "Download File"}
                    >
                        <FiDownload className="w-2.5 h-2.5" /> {binaryName || "File"}
                    </a>
                )}
            </div>
        );
    };

    // Helper to render endpoint pill
    const renderEndpoint = (value: string) => (
        <span
            title="Endpoint Value"
            className={`text-[10px] font-bold px-2 py-0.5 rounded border shadow-sm ${endpointClass}`}
        >
            {value}
        </span>
    );

    let asset = "";
    let targets: string[] = [];
    let endpoints: string[] = [];

    // Determine data source
    if (
        assetName ||
        (targetValues && targetValues.length > 0) ||
        (endpointValues && endpointValues.length > 0)
    ) {
        asset = assetName || programAssetName || programTitle || "Asset";
        targets = targetValues || [];
        endpoints = endpointValues || [];
    } else {
        // Fallback to parsing scope string
        const isStructuredPipe = scope?.includes(" | ");
        const isStructuredArrow = scope?.includes(" > ");
        const isStructured = isStructuredPipe || isStructuredArrow;

        if (!isStructured) {
            return (
                <div className="flex items-start gap-4">
                    {isDark && <img src={TargetingReportIcon} alt="Targeting" className="w-8 h-8 mt-1" />}
                    <div className="flex-1 space-y-2">
                        {showLabel && (
                            <span className={`${labelColor} text-base font-normal block mb-1`}>
                                Targeting:&nbsp;
                            </span>
                        )}
                        <div className="flex flex-wrap items-center gap-2">
                            {!isV1 && renderAsset(programAssetName || programTitle || "Asset")}
                            {!isV1 && <span className={`${separatorColor} self-center`}>|</span>}
                            <span
                                title="Target Value"
                                className={`text-[11px] font-medium px-2 py-0.5 rounded border ${targetClass}`}
                            >
                                {scope || "No Scope"}
                            </span>
                        </div>
                    </div>
                </div>
            );
        }

        const separator = isStructuredPipe ? " | " : " > ";
        const parts = scope?.split(separator).map(p => p.trim()) || [];
        asset = parts[0] || "Asset";
        targets = parts[1]
            ? parts[1]
                .split(",")
                .map(t => t.trim())
                .filter(t => t !== "")
            : [];
        endpoints = parts[2]
            ? parts[2]
                .split(",")
                .map(e => e.trim())
                .filter(e => e !== "")
            : [];
    }

    // ── Grouped display (when targetEndpointMap is provided) ──
    // Shows endpoints indented underneath their parent target
    const hasGroupedMap = targetEndpointMap && Object.keys(targetEndpointMap).length > 0;

    if (hasGroupedMap) {
        const targetsWithEndpoints = targets.filter(t => targetEndpointMap![t]?.length > 0);
        const targetsWithoutEndpoints = targets.filter(t => !targetEndpointMap![t]?.length);
        const allTargets = [...targetsWithEndpoints, ...targetsWithoutEndpoints];

        // ── Collapsed budget ──
        // Show at most VISIBLE_TARGETS_COLLAPSED targets and VISIBLE_ENDPOINTS_PER_TARGET
        // endpoints per target. Everything else is behind "Read more".
        const VISIBLE_TARGETS_COLLAPSED = 1;
        const VISIBLE_ENDPOINTS_PER_TARGET = 2;

        const totalEndpoints = Object.values(targetEndpointMap!).reduce((sum, eps) => sum + eps.length, 0);
        const needsReadMore =
            allTargets.length > VISIBLE_TARGETS_COLLAPSED ||
            totalEndpoints > VISIBLE_TARGETS_COLLAPSED * VISIBLE_ENDPOINTS_PER_TARGET;

        const visibleTargets = isExpanded ? allTargets : allTargets.slice(0, VISIBLE_TARGETS_COLLAPSED);
        const hiddenTargetCount = allTargets.length - visibleTargets.length;

        return (
            <div className="flex items-start gap-4">
                {isDark && <img src={TargetingReportIcon} alt="Targeting" className="w-8 h-8 mt-1" />}
                <div className="flex-1 space-y-2">
                    {showLabel && (
                        <span className={`${labelColor} text-base font-normal block mb-1`}>
                            Targeting:&nbsp;
                        </span>
                    )}
                    {/* Asset pill */}
                    {!isV1 && (
                        <div className="flex items-center gap-2 flex-wrap">
                            {renderAsset(asset)}
                        </div>
                    )}

                    {/* Targets (with grouped endpoints) */}
                    {visibleTargets.map(target => {
                        const eps = targetEndpointMap![target] || [];
                        const visibleEps = isExpanded ? eps : eps.slice(0, VISIBLE_ENDPOINTS_PER_TARGET);
                        const hiddenEpCount = eps.length - visibleEps.length;

                        return (
                            <div key={target} className={`ml-2 pl-3 border-l-2 ${isDark ? "border-indigo-700/40" : "border-indigo-200"}`}>
                                {/* Target pill */}
                                <div className="flex items-center gap-2 mb-1">
                                    {renderTarget(target)}
                                </div>
                                {/* Endpoints under this target */}
                                {visibleEps.length > 0 && (
                                    <div className="ml-3 flex flex-wrap gap-1.5 items-center mt-1">
                                        {visibleEps.map((ep, i) => (
                                            <div key={i}>{renderEndpoint(ep)}</div>
                                        ))}
                                        {/* Per-target hidden endpoint count (only shown collapsed) */}
                                        {!isExpanded && hiddenEpCount > 0 && (
                                            <span className={`text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"} font-medium`}>
                                                +{hiddenEpCount} more
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Hidden targets summary (collapsed state) */}
                    {!isExpanded && hiddenTargetCount > 0 && (
                        <span className={`text-[10px] ml-2 ${isDark ? "text-gray-400" : "text-gray-500"} font-medium`}>
                            +{hiddenTargetCount} more target{hiddenTargetCount > 1 ? "s" : ""}
                        </span>
                    )}

                    {/* Read More / Read Less */}
                    {needsReadMore && (
                        <div className="mt-1">
                            <button
                                onClick={() => setIsExpanded(!isExpanded)}
                                className={`text-xs font-bold transition-colors ${readMoreColor}`}
                            >
                                {isExpanded ? "Read less" : "Read more"}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ── Flat display (original behaviour, no map) ──
    const safeEndpoints = Array.isArray(endpoints) ? endpoints : [];
    const visibleEndpoints = isExpanded || safeEndpoints.length <= 1
        ? safeEndpoints
        : safeEndpoints.slice(0, 1);

    const remainingEndpointsCount = safeEndpoints.length - visibleEndpoints.length;
    const showReadMore = safeEndpoints.length > 1;

    return (
        <div className="flex items-start gap-4">
            {isDark && <img src={TargetingReportIcon} alt="Targeting" className="w-8 h-8 mt-1" />}
            <div className="flex-1">
                <div className="inline-flex flex-wrap items-center gap-2">
                    {showLabel && (
                        <span className={`${labelColor} text-base font-normal shrink-0`}>
                            Targeting:&nbsp;
                        </span>
                    )}
                    {/* Asset */}
                    {!isV1 && renderAsset(asset)}

                    {/* Targets */}
                    {targets.length > 0 && (
                        <>
                            {!isV1 && <span className={`${separatorColor} self-center`}>|</span>}
                            <div className="inline-flex flex-wrap gap-1.5 items-center">
                                {targets.map((target, i) => (
                                    <div key={i}>{renderTarget(target)}</div>
                                ))}
                            </div>
                        </>
                    )}

                    {/* Endpoints */}
                    {endpoints.length > 0 && (
                        <>
                            <span className={`${separatorColor} self-center`}>|</span>
                            <div className="inline-flex flex-wrap gap-1.5 items-center">
                                {visibleEndpoints.map((endpoint, i) => (
                                    <div key={i}>{renderEndpoint(endpoint)}</div>
                                ))}
                                {!isExpanded && remainingEndpointsCount > 0 && (
                                    <span className={`text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"} font-medium`}>
                                        +{remainingEndpointsCount} more
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Read More button */}
                {showReadMore && (
                    <div className="mt-1">
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className={`text-xs font-bold transition-colors ${readMoreColor}`}
                        >
                            {isExpanded ? "Read less" : "Read more"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ScopeDisplay;
